import { z } from 'zod';

// Base schemas for common patterns
export const idSchema = z.string().optional();
export const nameSchema = z.string()
  .min(1, 'validation.required')
  .max(100, 'validation.maxLength')
  .regex(/^[\p{L}][\p{L}\p{N}\s_-]*$/u, 'validation.nameFormat')
  .transform((val) => val.trim());
export const descriptionSchema = z.string().max(500, 'validation.descriptionMaxLength').nullish().transform(val => {
  if (val === null || val === undefined || val === '') {
    return undefined;
  }
  return val;
});

// Attribute Definition Schema
export const attributeDefinitionSchema = z.object({
  uuid: z.string().optional(),
  name: nameSchema,
  valueType: z.enum(['MATERIAL', 'NUMBER', 'ENUMERATION'], {
    message: 'validation.invalidValueType'
  }),
  description: descriptionSchema,
  min: z.number().min(0, 'validation.minValueZero').optional(),
  max: z.number().min(0, 'validation.minValueZero').optional(),
  valueList: z.string().optional(),
}).refine((data) => {
  // If valueType is ENUMERATION, valueList should be provided
  if (data.valueType === 'ENUMERATION' && (!data.valueList || data.valueList.trim() === '')) {
    return false;
  }
  return true;
}, {
  message: 'validation.valueListRequiredForEnum',
  path: ['valueList']
}).refine((data) => {
  // If min and max are both provided, max should be greater than min
  if (data.min !== undefined && data.max !== undefined && data.max <= data.min) {
    return false;
  }
  return true;
}, {
  message: 'validation.maxGreaterThanMin',
  path: ['max']
});

// Resource Type Schema
export const resourceTypeSchema = z.object({
  uuid: z.string().optional(),
  name: nameSchema,
  description: descriptionSchema,
  singleResource: z.boolean().optional().default(false),
  batchProcessing: z.boolean().optional().default(false),
  synchronicity: z.boolean().optional().default(false),
  category: z.enum(['OVEN', 'PROCESSING_LINE', 'OTHERS'], {
    required_error: 'validation.required',
    invalid_type_error: 'validation.invalidCategory'
  }),
  ovenType: z.union([
    z.enum(['STONE_OVEN', 'RACK_OVEN', 'CONTINUOUS_OVEN']),
    z.null()
  ]).optional(),
  numStoves: z.union([z.number().int().min(1, 'validation.minValue'), z.null()]).optional(),
  heatingPhaseSecondsPerDegree: z.union([z.number().int().min(0, 'validation.minValue'), z.null()]).optional(),
  coolingPhaseSecondsPerDegree: z.union([z.number().int().min(0, 'validation.minValue'), z.null()]).optional(),
  setupAttributes: z.array(z.any()).optional().default([]),
  batchingAttributes: z.array(z.any()).optional().default([]),
}).superRefine((data, ctx) => {
  // ovenType should only be validated when category is OVEN
  if (data.category === 'OVEN') {
    if (!data.ovenType || data.ovenType === null || 
        (data.ovenType !== 'STONE_OVEN' && data.ovenType !== 'RACK_OVEN' && data.ovenType !== 'CONTINUOUS_OVEN')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'validation.required',
        path: ['ovenType']
      });
    }
  }
  // If category is not OVEN, ovenType can be null or undefined (no validation needed)
}).refine((data) => {
  // When ovenType = STONE_OVEN, numStoves must be > 0
  if (data.ovenType === 'STONE_OVEN') {
    if (!data.numStoves || data.numStoves <= 0) {
      return false;
    }
  }
  return true;
}, {
  message: 'validation.numStovesRequiredForStoneOven',
  path: ['numStoves']
}).refine((data) => {
  // OVEN and PROCESSING_LINE categories must have singleResource = true
  if (data.category === 'OVEN' || data.category === 'PROCESSING_LINE') {
    if (!data.singleResource) {
      return false;
    }
  }
  return true;
}, {
  message: 'validation.singleResourceRequiredForSetupTimes',
  path: ['singleResource']
}).refine((data) => {
  // If category is OVEN, heatingPhaseSecondsPerDegree is required
  if (data.category === 'OVEN') {
    if (data.heatingPhaseSecondsPerDegree === null || data.heatingPhaseSecondsPerDegree === undefined) {
      return false;
    }
  }
  return true;
}, {
  message: 'validation.heatingCoolingPhaseRequiredForOven',
  path: ['heatingPhaseSecondsPerDegree']
}).refine((data) => {
  // If category is OVEN, coolingPhaseSecondsPerDegree is required
  if (data.category === 'OVEN') {
    if (data.coolingPhaseSecondsPerDegree === null || data.coolingPhaseSecondsPerDegree === undefined) {
      return false;
    }
  }
  return true;
}, {
  message: 'validation.heatingCoolingPhaseRequiredForOven',
  path: ['coolingPhaseSecondsPerDegree']
}).refine((data) => {
  // Synchronicity can only be true when both singleResource and batchProcessing are true
  if (data.synchronicity && (!data.singleResource || !data.batchProcessing)) {
    return false;
  }
  return true;
}, {
  message: 'validation.synchronicityRequiresBoth',
  path: ['synchronicity']
});

// Export types derived from schemas
export type AttributeDefinitionFormData = z.infer<typeof attributeDefinitionSchema>;
export type ResourceTypeFormData = z.infer<typeof resourceTypeSchema>;

// Validation helper functions
export const validateAttributeDefinition = (data: unknown): AttributeDefinitionFormData => {
  return attributeDefinitionSchema.parse(data);
};

export const validateResourceType = (data: unknown): ResourceTypeFormData => {
  return resourceTypeSchema.parse(data);
};

// Safe validation functions that return errors instead of throwing
export const safeValidateAttributeDefinition = (data: unknown) => {
  return attributeDefinitionSchema.safeParse(data);
};

export const safeValidateResourceType = (data: unknown) => {
  return resourceTypeSchema.safeParse(data);
}; 